import Select, { type FilterOptionOption } from "react-select";
import { useTranslation } from "react-i18next";
import { useTheme } from "context/ThemeContext";
import { useSettings } from "context/SettingsContext";
import { selectStylesDark, selectStylesLigth } from "utils/helpers/structs/selectStyles";
import { useEffect, useState } from "react";
import vconst from "utils/helpers/structs/vconst";
import { isActiveTeamWarehouse } from "utils/helpers/teams/isTeamActive";
import { isActiveCustomWarehouse } from "utils/helpers/warehouses/isCustomWarehouseActive";
import useSettingsParams from "hooks/useSettingsParams";
import WarehouseSelectHeader from "./WarehouseSelectHeader";
import type { WarehouseInfo } from "@econtractor/types";

type WarehouseOptionType = 'main' | 'writeOff' | 'team' | 'object' | 'reserve' | 'custom';

type WarehouseOption = {
    value: number;
    label: string;
    type: WarehouseOptionType;
    info?: string;
};

type WarehouseSelectProps = {
    title?: string;
    onChange?: (option: WarehouseOption | null) => void;
    value?: number | null;
    disabled?: boolean;
    required?: boolean;
    onlyForTeamId?: number | null;
    /** Тільки склад бригади з цим teamId */
    onlyTeamId?: number | null;
    /** Тільки склад об'єкта з цим objectId */
    onlyObjectId?: number | null;
    showWarehouseMain?: boolean;
    showWarehouseWriteOff?: boolean;
    showWarehouseTeam?: boolean;
    showWarehouseObject?: boolean;
    showWarehouseReserve?: boolean;
    showWarehouseCustom?: boolean;
    hideFilter?: boolean;
    hideWarehouses?: number[];
    menuPlacement?: 'auto' | 'bottom' | 'top';
    menuPosition?: 'absolute' | 'fixed';
    menuPortalTarget?: HTMLElement | null;
};

const WarehouseSelect = ({
    title,
    onChange,
    value,
    disabled,
    required = false,
    onlyForTeamId = null,
    onlyTeamId = null,
    onlyObjectId = null,
    showWarehouseMain = true,
    showWarehouseWriteOff = true,
    showWarehouseTeam = true,
    showWarehouseObject = true,
    showWarehouseReserve = true,
    showWarehouseCustom = true,
    hideFilter = false,
    hideWarehouses = [],
    menuPlacement = 'auto',
    menuPosition,
    menuPortalTarget,
}: WarehouseSelectProps) => {

    const { t } = useTranslation(['Warehouses']);
    const { darkTheme } = useTheme();
    const { localSettings, getSettings } = useSettings();
    const params = useSettingsParams();
    const customEnabled = Boolean(params.customWarehouses);
    const [selectTheme, setSelectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);
    const [options, setOptions] = useState<WarehouseOption[]>([]);
    const [warehouseTypeFilter, setWarehouseTypeFilter] = useState('all');

    const fetchWarehouses = () => {
        const warehouses = (localSettings?.WAREHOUSES || []) as WarehouseInfo[];
        const activeObjectStatuses = [vconst.objectInWork, vconst.objectPanding];
        const allWarehouses = [
            ...warehouses.filter((wh) => showWarehouseMain && wh.ID === vconst.waherouseMainId),
            ...warehouses.filter((wh) => showWarehouseWriteOff && wh.ID === vconst.waherouseWriteOffId),
            ...warehouses.filter((wh) =>
                showWarehouseCustom &&
                customEnabled &&
                isActiveCustomWarehouse(wh)
            ),
            ...warehouses.filter((wh) =>
                showWarehouseTeam &&
                wh.teamId &&
                isActiveTeamWarehouse(wh, localSettings?.DIRS?.Team) &&
                (!onlyTeamId || wh.teamId === onlyTeamId)
            ),
            ...warehouses.filter((wh) =>
                showWarehouseReserve &&
                Number(wh.reservObjectId) > 0 &&
                activeObjectStatuses.includes(wh.objectStatus as number)
            ),
            ...warehouses.filter((wh) =>
                showWarehouseObject &&
                wh.objectId &&
                !Number(wh.reservObjectId) &&
                !Number(wh.customId) &&
                (!onlyObjectId || Number(wh.objectId) === Number(onlyObjectId)) &&
                (onlyObjectId || activeObjectStatuses.includes(wh.objectStatus as number)) &&
                (!onlyForTeamId || wh.objectTeamId === onlyForTeamId)
            ),
        ];

        setOptions(
            allWarehouses
                .filter((wh) => !hideWarehouses.includes(wh.ID))
                .map((wh): WarehouseOption => {
                    if (Number(wh.reservObjectId) > 0) {
                        return {
                            value: wh.ID,
                            label: wh.objectName || wh.clientName || '',
                            type: 'reserve',
                            info: wh.clientName,
                        };
                    }

                    if (isActiveCustomWarehouse(wh) || Number(wh.customId) > 0) {
                        return {
                            value: wh.ID,
                            label: wh.customName || `#${wh.customId}`,
                            type: 'custom',
                        };
                    }

                    if (
                        !wh.teamId &&
                        !wh.objectId &&
                        (wh.ID === vconst.waherouseMainId || wh.ID === vconst.waherouseWriteOffId)
                    ) {
                        return {
                            value: wh.ID,
                            label: wh.ID == vconst.waherouseMainId ? t('mainWarehouse') : t('writeOffWarehouse'),
                            type: wh.ID == vconst.waherouseMainId ? 'main' : 'writeOff',
                        };
                    }

                    return wh.teamId
                        ? { value: wh.ID, label: wh.teamName || '', type: 'team' }
                        : {
                            value: wh.ID,
                            label: wh.objectName || wh.clientName || '',
                            type: 'object',
                            info: wh.clientName,
                        };
                })
        );
    };

    useEffect(() => {
        void getSettings();
        return () => setOptions([]);
    }, []);

    useEffect(() => {
        fetchWarehouses();
    }, [localSettings, customEnabled]);

    useEffect(() => {
        setSelectTheme(darkTheme ? selectStylesDark : selectStylesLigth);
    }, [darkTheme]);

    const filterOption = (option: FilterOptionOption<WarehouseOption>, inputValue: string) => {
        if (!inputValue) return true;
        return option?.data?.label?.toLowerCase()?.includes(inputValue?.toLowerCase());
    };

    const filteredOptions = options.filter((wh) => {
        if (warehouseTypeFilter === 'main') {
            return wh.type === 'main' || wh.type === 'writeOff' || wh.type === 'custom';
        }
        if (warehouseTypeFilter === 'team') return wh.type === 'team';
        if (warehouseTypeFilter === 'object') return wh.type === 'object';
        if (warehouseTypeFilter === 'reserve') return wh.type === 'reserve';
        return true;
    });

    return (
        <div className="mb-3">
            <WarehouseSelectHeader
                title={title}
                hideFilter={hideFilter}
                warehouseTypeFilter={warehouseTypeFilter}
                setWarehouseTypeFilter={setWarehouseTypeFilter}
                t={t}
            />

            <Select<WarehouseOption>
                options={filteredOptions}
                isDisabled={disabled}
                required={required}
                menuPlacement={menuPlacement}
                menuPosition={menuPosition}
                menuPortalTarget={menuPortalTarget}
                styles={{
                    ...selectTheme,
                    control: (base, state) => ({
                        ...base,
                        ...(selectTheme.control ? selectTheme.control(base, state) : {}),
                    }),
                    ...(menuPortalTarget ? {
                        menu: (base) => ({
                            ...base,
                            ...(selectTheme.menu ? selectTheme.menu(base) : {}),
                            zIndex: 99999,
                        }),
                        menuPortal: (base) => ({
                            ...base,
                            zIndex: 99999,
                        }),
                    } : {}),
                }}
                className="w-100"
                value={filteredOptions.find((opt) => opt.value == value) || null}
                onChange={onChange}
                filterOption={filterOption}
                isSearchable={true}
                isClearable={true}
                getOptionLabel={(e) => e.label}
                formatOptionLabel={(e) => {
                    let textClass = '';
                    switch (e.type) {
                        case 'main': textClass = 'text-info'; break;
                        case 'object': textClass = 'text-success'; break;
                        case 'team': textClass = 'text-primary'; break;
                        case 'reserve': textClass = 'text-warning'; break;
                        case 'custom': textClass = 'text-info'; break;
                        case 'writeOff': textClass = 'text-danger'; break;
                    }
                    return (<>
                        <span className={`fw-medium ${textClass}`}>{e.label}</span>
                        <span className={`d-block fs-12 text-muted`}>
                            {e.type == 'team' && t('teamWarehouse')}
                            {e.type == 'object' && e.info}
                            {e.type == 'reserve' && t('reserveWarehouse')}
                            {e.type == 'custom' && t('customWarehouse')}
                        </span>
                    </>);
                }}
            />
        </div>
    );
};

export default WarehouseSelect;
